// src/services/queries/mediaInsights/newsQueries.ts

import type { QueryFunctionContext } from "@tanstack/react-query";
import { queryKeys } from "@/constants/queryKeys";
import type {
  FetchNewsParams,
  NewsAPIResponse,
  News,
} from "@/types/mediaInsights/news";
import { TIME } from "@/constants/app";
import { API_BASE_URL } from "@/services";

/**
 * Query function for fetching news data with optional filters
 */
export const fetchNews = async ({
  signal,
  queryKey,
}: QueryFunctionContext<
  ReturnType<typeof queryKeys.mediaInsights.news>
>): Promise<News[]> => {
  const [, , params] = queryKey;

  // Build the request body from params
  const body: FetchNewsParams = {};

  if (params?.limit !== undefined) {
    body.limit = params.limit;
  }

  if (params?.offset !== undefined) {
    body.offset = params.offset;
  }

  if (params?.start_datetime) {
    body.start_datetime = params.start_datetime;
  }

  if (params?.end_datetime) {
    body.end_datetime = params.end_datetime;
  }

  const init: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  };

  if (signal) {
    init.signal = signal;
  }

  const response = await fetch(
    `${API_BASE_URL}/multimediaInsightPage/getNews`,
    init
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const result: NewsAPIResponse = await response.json();
  return result.data;
};

/**
 * Query options factory for news data
 */
export const newsQueryOptions = (params?: FetchNewsParams) => ({
  queryKey: queryKeys.mediaInsights.news(params),
  queryFn: fetchNews,
  staleTime: 5 * TIME.MINUTE, // 5 minutes - data considered fresh
  gcTime: 30 * TIME.MINUTE, // 30 minutes - garbage collection time
  refetchOnWindowFocus: false, // Don't refetch on window focus
  retry: (failureCount: number, error: Error) => {
    // Don't retry on 4xx errors
    if (error.message.includes("4")) {
      return false;
    }
    return failureCount < 3;
  },
});
